import React from 'react';
import { Box, Typography, Chip } from '@weave-mui/material';

/**
 * ScheduleDisplay - A reusable component for displaying schedule information
 * Shows a schedule chip and optional time text based on cron expression and dates
 *
 * @param {Object} props - Component props
 * @param {string|null} [props.scheduledtime=null] - AWS EventBridge cron expression (6 fields)
 * @param {string|null} [props.start_date=null] - Fallback start date in ISO format
 * @param {string|null} [props.end_date=null] - End date in ISO format (currently unused)
 * @param {boolean} [props.showTime=true] - Whether to display the time alongside the schedule chip
 * @param {'horizontal'|'vertical'} [props.orientation='horizontal'] - Layout orientation
 * @param {'small'|'medium'|'large'} [props.size='medium'] - Size variant for the chip
 * @returns {JSX.Element} The rendered schedule display component
 *
 * @example
 * // Basic usage
 * <ScheduleDisplay scheduledtime="0 8 * * 1 *" />
 *
 * @example
 * // With custom options
 * <ScheduleDisplay
 *   scheduledtime="0 8 * * 1 *"
 *   showTime={true}
 *   orientation="vertical"
 *   size="large"
 * />
 */
const ScheduleDisplay = ({
  scheduledtime = null,
  start_date = null,
  end_date = null,
  showTime = true,
  orientation = 'horizontal',
  size = 'medium',
}) => {
  // Format schedule based on cron expression - using enhanced logic from drawer
  const formatSchedule = (scheduledtime, start_date, end_date) => {
    if (!scheduledtime) return 'Schedule not set';

    // Parse AWS cron expression: minute hour day month day_of_week year
    const cronParts = scheduledtime.trim().split(/\s+/);
    if (cronParts.length !== 6) return 'Custom';

    const [minute, hour, day, month, dayOfWeek, year] = cronParts;

    // Check for complex expressions that indicate custom scheduling
    const hasComplexExpression = [
      minute,
      hour,
      day,
      month,
      dayOfWeek,
      year,
    ].some(
      (part) =>
        part.includes('/') ||
        part.includes('-') ||
        part.includes(',') ||
        part.includes('L') ||
        part.includes('W') ||
        part.includes('#')
    );

    if (hasComplexExpression) return 'Custom';

    // Weekly schedule (specific day_of_week, day is * or ?)
    if (
      dayOfWeek !== '*' &&
      dayOfWeek !== '?' &&
      (day === '*' || day === '?') &&
      month === '*'
    ) {
      return 'Weekly';
    }

    // Daily schedule (day, month, and dayOfWeek are * or ?)
    if (
      (day === '*' || day === '?') &&
      month === '*' &&
      (dayOfWeek === '*' || dayOfWeek === '?')
    ) {
      return 'Daily';
    }

    // Monthly schedule (specific day, month and dayOfWeek are * or ?)
    if (
      day !== '*' &&
      day !== '?' &&
      month === '*' &&
      (dayOfWeek === '*' || dayOfWeek === '?')
    ) {
      return 'Monthly';
    }

    return 'Custom';
  };

  // Get schedule time - using enhanced logic from drawer
  const getScheduleTime = (scheduledtime, start_date, end_date) => {
    if (!scheduledtime) return '';

    // Parse AWS cron expression: minute hour day month day_of_week year
    const cronParts = scheduledtime.trim().split(/\s+/);
    if (cronParts.length !== 6) return '';

    const [minute, hour, day, month, dayOfWeek] = cronParts;

    // Helper functions
    const parseNumericValue = (value) => {
      const match = value.match(/\d+/);
      return match ? parseInt(match[0]) : null;
    };

    const formatTime = (h, m = '0') => {
      const hourNum = parseNumericValue(h) || 0;
      const minuteNum = parseNumericValue(m) || 0;

      // Convert to 12-hour format
      const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
      const ampm = hourNum >= 12 ? 'pm' : 'am';
      const minuteStr =
        minuteNum > 0 ? `:${minuteNum.toString().padStart(2, '0')}` : '';

      return `${hour12}${minuteStr} ${ampm}`;
    };

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Weekly schedule
    if (
      dayOfWeek !== '*' &&
      dayOfWeek !== '?' &&
      (day === '*' || day === '?') &&
      month === '*'
    ) {
      const dayNum = parseNumericValue(dayOfWeek);
      const dayName = dayNum !== null ? dayNames[dayNum] : '';
      return `${dayName}, ${formatTime(hour, minute)} UTC`;
    }

    // Daily schedule
    if (
      (day === '*' || day === '?') &&
      month === '*' &&
      (dayOfWeek === '*' || dayOfWeek === '?')
    ) {
      return `${formatTime(hour, minute)} UTC`;
    }

    // Monthly schedule
    if (
      day !== '*' &&
      day !== '?' &&
      month === '*' &&
      (dayOfWeek === '*' || dayOfWeek === '?')
    ) {
      const dayNum = parseNumericValue(day);
      const ordinal = getOrdinal(dayNum);
      return `${dayNum}${ordinal}, ${formatTime(hour, minute)} UTC`;
    }

    // Custom or unrecognized pattern
    return start_date ? new Date(start_date).toLocaleString() : '';
  };

  // Helper function to get ordinal suffix
  const getOrdinal = (num) => {
    if (!num) return '';
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  // Render schedule chip with consistent styling
  const renderScheduleChip = (schedule) => {
    const getChipStyles = (scheduleType) => {
      if (scheduleType === 'Schedule not set') {
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.15)',
          '&:hover': {
            backgroundColor: 'rgba(128, 128, 128, 0.25)',
          },
        };
      }
      // All active schedules (Weekly, Daily, Monthly, Custom) use blue
      return {
        backgroundColor: 'rgba(155, 213, 239, 1)',
        '&:hover': {
          backgroundColor: 'rgba(155, 213, 239)',
        },
      };
    };

    // Size variants
    const sizeStyles = {
      small: {
        minWidth: 60,
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
        lineHeight: '12px',
      },
      medium: {
        minWidth: 70,
        height: 24,
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '14px',
      },
      large: {
        minWidth: 80,
        height: 28,
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: '16px',
      },
    };

    return (
      <Chip
        label={schedule}
        size="small"
        variant="filled"
        sx={{
          ...sizeStyles[size],
          ...getChipStyles(schedule),
        }}
      />
    );
  };

  const schedule = formatSchedule(scheduledtime, start_date, end_date);
  const scheduleTime = showTime
    ? getScheduleTime(scheduledtime, start_date, end_date)
    : '';

  // Layout based on orientation
  if (orientation === 'vertical') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {renderScheduleChip(schedule)}
        {scheduleTime && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {scheduleTime}
          </Typography>
        )}
      </Box>
    );
  }

  // Horizontal layout (default)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {renderScheduleChip(schedule)}
      {scheduleTime && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {scheduleTime}
        </Typography>
      )}
    </Box>
  );
};

export default ScheduleDisplay;
